import {ReactElement, useState} from "react";
import {
    ClientAuthenticationType,
    useCreateUserMutation,
    useGetClientAuthConfigurationQuery,
    UserRole,
} from "../../../__generated__/generated_types";

import {Close, Modal} from "@zendeskgarden/react-modals";
import {toast} from "react-toastify";
import UserForm, {TUserRecord} from "./components/user-form/UserForm";
import {Dots} from "@zendeskgarden/react-loaders";

export interface ICreateUser {
    formVisible: boolean,
    onModalClose: () => void,
    onCreate: () => void,
}

export function UserCreateModal(props: ICreateUser): ReactElement {

    const {formVisible, onModalClose, onCreate} = props;

    const {data: clientAuthConfig, loading: loadingClientAuthConfig} = useGetClientAuthConfigurationQuery({
        fetchPolicy: "no-cache"
    });
    const forceGeneratePassword = clientAuthConfig?.getClientAuthConfiguration?.loginType === ClientAuthenticationType.SingleSignOn;

    const [saveUser, {loading: updateUserLoading}] = useCreateUserMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data) {
                toast.success(`${data.createUser.firstName} ${data.createUser.lastName} created`);
                onCreate();
            }
            reset();
        },
    });

    const [user, setUser] = useState<TUserRecord>({
        firstName: "",
        lastName: "",
        email: "",
        generatePassword: true,
        password: "",
        role: UserRole.Manager,
        isReadOnly: false,
        authorizations: [],
        properties: [],
        groups: [],
    });


    function updateUser(update: Partial<TUserRecord>): void {
        if (user) {
            let adminOverrides: Partial<TUserRecord> = {};
            if (user.role == UserRole.Admin) {
                adminOverrides = {
                    isReadOnly: false,
                    groups: [],
                    authorizations: [],
                    properties: [],
                };
            }

            // SSO Requires we set generatePassword to true.
            if(forceGeneratePassword) {
                adminOverrides["generatePassword"] = true;
            }

            setUser({
                ...user,
                ...update,
                ...adminOverrides,
            });
        }
    }

    function doSaveUser(): void {
        if (user && user.email != undefined && (user.generatePassword || user.password != undefined)) {
            if (user.role == UserRole.Admin) {
                user.isReadOnly = false;
                user.groups = [];
                user.authorizations = [];
                user.properties = [];
            }

            saveUser({
                variables: {
                    firstName: user.firstName,
                    lastName: user.lastName,
                    role: user.role,
                    email: user.email,
                    generatePassword: forceGeneratePassword ? true : (user.generatePassword ?? false),
                    password: forceGeneratePassword ? undefined : (user.password === "" ? undefined : user.password),
                    isReadOnly: user.isReadOnly,
                    authorizations: user.authorizations,
                    properties: user.properties.map(p => p.id),
                    groups: user.groups.map(g => g.id)
                }
            }).catch(() => { /* Do Nothing */ });
        }
    }

    function reset(): void {
        setUser({
            firstName: "",
            lastName: "",
            email: "",
            generatePassword: true,
            password: "",
            role: UserRole.Manager,
            isReadOnly: false,
            authorizations: [],
            properties: [],
            groups: [],
        });
        onModalClose();
    }

    if (!formVisible) {
        return <></>;
    }

    return (
        <Modal onClose={reset}>
            {(loadingClientAuthConfig || !clientAuthConfig?.getClientAuthConfiguration) ?
                <Dots size={32} /> :
                <UserForm
                    isCreate
                    forceGeneratePassword={forceGeneratePassword}
                    user={user}
                    loading={updateUserLoading}
                    onChange={updateUser}
                    onSubmit={doSaveUser}
                    onClose={reset}
                />
            }
            <Close aria-label="Close modal" />
        </Modal>
    );
}
